import React, { useState, useEffect } from 'react';
import { Mail, Plus, Trash2 } from 'lucide-react';
import { supabase, PlatformConfig } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const PlatformConfiguration: React.FC = () => {
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [juryVotingEnabled, setJuryVotingEnabled] = useState(false);
  const [publicVotingEnabled, setPublicVotingEnabled] = useState(false);
  const [nominationsEditingEnabled, setNominationsEditingEnabled] = useState(true);
  const [nominationsSubmissionEnabled, setNominationsSubmissionEnabled] = useState(true);
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const { isNovagobTeam, user } = useAuth();

  useEffect(() => {
    if (isNovagobTeam) loadConfig();
  }, [isNovagobTeam]);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .schema('premios')
        .from('platform_config')
        .select('*')
        .single();
      if (error) throw error;
      setConfig(data);
      setJuryVotingEnabled(data.jury_voting_enabled);
      setPublicVotingEnabled(data.public_voting_enabled);
      setNominationsEditingEnabled(data.nominations_editing_enabled ?? true);
      setNominationsSubmissionEnabled(data.nominations_submission_enabled ?? true);
      setNotifyEmails(
        Array.isArray(data.new_nomination_notify_emails)
          ? data.new_nomination_notify_emails
          : []
      );
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const isValidEmail = (s: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

  const addNotifyEmail = async () => {
    setEmailError('');
    const value = newEmail.trim().toLowerCase();
    if (!isValidEmail(value)) {
      setEmailError('Introduce un email válido.');
      return;
    }
    if (notifyEmails.map((e) => e.toLowerCase()).includes(value)) {
      setEmailError('Ese correo ya está en la lista.');
      return;
    }
    const next = [...notifyEmails, value];
    setNotifyEmails(next);
    setNewEmail('');
    await persist({ new_nomination_notify_emails: next } as any);
  };

  const removeNotifyEmail = async (email: string) => {
    const next = notifyEmails.filter((e) => e !== email);
    setNotifyEmails(next);
    await persist({ new_nomination_notify_emails: next } as any);
  };

  const persist = async (patch: Partial<PlatformConfig>) => {
    if (!config) return;
    setSaving(true);
    setMessage('');
    try {
      const { error } = await supabase
        .schema('premios')
        .from('platform_config')
        .update({
          ...patch,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', config.id);
      if (error) throw error;
      setMessage('Configuración actualizada.');
      loadConfig();
      setTimeout(() => setMessage(''), 2500);
    } catch (err: any) {
      console.error(err);
      setMessage('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleJury = () => {
    const next = !juryVotingEnabled;
    setJuryVotingEnabled(next);
    persist({ jury_voting_enabled: next });
  };

  const togglePublic = () => {
    const next = !publicVotingEnabled;
    setPublicVotingEnabled(next);
    persist({ public_voting_enabled: next });
  };

  const toggleEditing = () => {
    const next = !nominationsEditingEnabled;
    setNominationsEditingEnabled(next);
    persist({ nominations_editing_enabled: next });
  };

  const toggleSubmission = () => {
    const next = !nominationsSubmissionEnabled;
    setNominationsSubmissionEnabled(next);
    persist({ nominations_submission_enabled: next });
  };

  if (!isNovagobTeam) {
    return (
      <div className="container">
        <div className="card">
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  type CardProps = {
    label: string;
    enabled: boolean;
    onToggle: () => void;
    enabledText?: string;
    disabledText?: string;
    extra?: React.ReactNode;
  };

  const ToggleCard: React.FC<CardProps> = ({
    label,
    enabled,
    onToggle,
    enabledText = 'Habilitada',
    disabledText = 'Deshabilitada',
    extra,
  }) => (
    <div
      className="stat-card"
      onClick={() => { if (!saving) onToggle(); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !saving) {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        cursor: saving ? 'wait' : 'pointer',
        border: `1.5px solid ${enabled ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
        transition: 'all 0.2s',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div className="stat-label" style={{ textAlign: 'left' }}>{label}</div>
        {extra}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: enabled ? 'var(--color-success)' : 'var(--color-error)',
        }}
      >
        {enabled ? `✓ ${enabledText}` : `✗ ${disabledText}`}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
        Pulsa para {enabled ? 'desactivar' : 'activar'}
      </div>
    </div>
  );

  return (
    <div className="container dashboard" style={{ padding: '40px 20px' }}>
      <div className="dashboard-header" style={{ marginBottom: '32px' }}>
        <h1>Configuración de la Plataforma</h1>
        <p>Panel de Control</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <h2 style={{ margin: 0 }}>Estado Actual</h2>
          {config && (
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Última actualización: {new Date(config.updated_at).toLocaleString('es-ES')}
            </span>
          )}
        </div>

        {message && (
          <div
            className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}
            style={{ marginBottom: 16 }}
          >
            {message}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <ToggleCard
            label="Votación del Jurado"
            enabled={juryVotingEnabled}
            onToggle={toggleJury}
          />

          <ToggleCard
            label="Votación Pública"
            enabled={publicVotingEnabled}
            onToggle={togglePublic}
          />

          <ToggleCard
            label="Envío de candidaturas"
            enabled={nominationsSubmissionEnabled}
            onToggle={toggleSubmission}
            enabledText="Abierto"
            disabledText="Cerrado"
          />

          <ToggleCard
            label="Editar candidaturas"
            enabled={nominationsEditingEnabled}
            onToggle={toggleEditing}
            enabledText="Permitido"
            disabledText="Bloqueado"
          />
        </div>
      </div>

      {/* Avisos automáticos por correo al recibir una nueva candidatura */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Mail size={20} style={{ color: 'var(--color-secondary)' }} />
          <h2 style={{ margin: 0 }}>Avisos de candidaturas</h2>
        </div>
        <p
          style={{
            margin: '0 0 16px',
            color: 'var(--color-text-secondary)',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          Las personas añadidas aquí recibirán un correo automático cada vez
          que se presente una nueva candidatura. El aviso incluye la
          <strong style={{ color: 'var(--color-text)' }}> categoría</strong> a
          la que se ha presentado y el
          <strong style={{ color: 'var(--color-text)' }}> nombre de quien la
          presenta</strong>.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 14,
            flexWrap: 'wrap',
          }}
        >
          <input
            type="email"
            className="form-input"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNotifyEmail();
              }
            }}
            placeholder="ejemplo@dominio.org"
            disabled={saving}
            style={{ flex: '1 1 240px', minWidth: 240 }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={addNotifyEmail}
            disabled={saving || !newEmail.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} />
            Añadir
          </button>
        </div>

        {emailError && (
          <div
            className="alert alert-error"
            style={{ marginBottom: 14, fontSize: 13, padding: '10px 12px' }}
          >
            {emailError}
          </div>
        )}

        {notifyEmails.length === 0 ? (
          <div
            style={{
              padding: 18,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.03)',
              border: '1px dashed rgba(255,255,255,0.1)',
              color: 'var(--color-text-secondary)',
              fontSize: 13.5,
              textAlign: 'center',
            }}
          >
            Aún no hay direcciones configuradas. Añade al menos una para
            recibir avisos cuando se presenten candidaturas.
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {notifyEmails.map((email) => (
              <li
                key={email}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '10px 14px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    color: 'var(--color-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  <Mail size={14} style={{ color: 'var(--color-secondary)', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</span>
                </span>
                <button
                  type="button"
                  onClick={() => removeNotifyEmail(email)}
                  aria-label={`Eliminar ${email}`}
                  title="Eliminar"
                  disabled={saving}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    color: 'var(--color-error)',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PlatformConfiguration;
